import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, careTypeLabel, facilityTypeLabel, specialtyLabel, tagLabel, fileUrl, CARE_TYPES, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, DollarSign, Heart, CheckCircle2, ArrowLeft } from "lucide-react";

const PLACEHOLDER =
  "https://images.unsplash.com/photo-1758691031197-3ce709db9640?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxjb3p5JTIwbGl2aW5nJTIwcm9vbSUyMHNlbmlvciUyMGNvbW11bml0eXxlbnwwfHx8fDE3ODAxNDU3NzB8MA&ixlib=rb-4.1.0&q=85&w=1200";

export default function FacilityDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [facility, setFacility] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    care_type: "drop_in",
    requested_date: "",
    loved_one_name: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [activePhoto, setActivePhoto] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/facilities/${id}`);
        setFacility(data);
      } catch (e) {
        toast.error("Facility not found");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) return <div className="px-6 py-20 text-center text-muted-foreground">Loading…</div>;
  if (!facility) return <div className="px-6 py-20 text-center">Facility not found.</div>;

  const photos = facility.photos && facility.photos.length > 0
    ? facility.photos.map((p) => fileUrl(p.path))
    : [PLACEHOLDER];

  const submit = async (e) => {
    e.preventDefault();
    if (!user) {
      navigate(`/login?next=/facility/${id}`);
      return;
    }
    if (user.role !== "caregiver") {
      toast.error("Only caregiver accounts can send inquiries.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/inquiries", { facility_id: id, ...form });
      setSubmitted(true);
      toast.success("Inquiry sent — the facility will reach out soon.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send inquiry");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="facility-detail-page" className="mx-auto max-w-7xl px-6 py-8 lg:px-8">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate(-1)}
        className="mb-4 rounded-full text-muted-foreground hover:text-foreground"
        data-testid="back-btn"
      >
        <ArrowLeft className="mr-1.5 h-4 w-4" /> Back to results
      </Button>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="md:col-span-2 overflow-hidden rounded-2xl border border-border/60">
          <img
            src={photos[activePhoto]}
            alt={facility.name}
            className="aspect-[16/10] w-full object-cover"
          />
        </div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-1">
          {photos.slice(0, 4).map((p, i) => (
            <button
              key={i}
              data-testid={`photo-thumb-${i}`}
              onClick={() => setActivePhoto(i)}
              className={`overflow-hidden rounded-2xl border ${
                activePhoto === i ? "border-primary ring-2 ring-primary/30" : "border-border/60"
              }`}
            >
              <img src={p} alt="" className="aspect-[5/3] w-full object-cover" />
            </button>
          ))}
        </div>
      </div>

      <div className="mt-10 grid gap-12 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Badge className="rounded-full bg-secondary text-secondary-foreground">
            {facilityTypeLabel(facility.facility_type)}
          </Badge>
          <h1 className="mt-3 font-serif text-4xl text-forest sm:text-5xl">{facility.name}</h1>
          <p className="mt-2 flex items-center gap-1.5 text-muted-foreground">
            <MapPin className="h-4 w-4" />
            {facility.address ? `${facility.address}, ` : ""}
            {facility.city}, {facility.state} {facility.zip}
          </p>

          {(facility.specialties || []).length > 0 && (
            <div className="mt-5 flex flex-wrap gap-2" data-testid="facility-specialties">
              {(facility.specialties || []).map((s) => (
                <span key={s} className="pill border border-emerald/30 bg-emerald/10 text-emerald font-medium">
                  {specialtyLabel(s)}
                </span>
              ))}
            </div>
          )}

          <div className="mt-3 flex flex-wrap gap-2">
            {(facility.care_types || []).map((c) => (
              <span key={c} className="pill bg-secondary text-secondary-foreground">
                {careTypeLabel(c)}
              </span>
            ))}
          </div>

          <div className="mt-8 prose max-w-none text-base leading-relaxed text-muted-foreground">
            {facility.description || "No description provided."}
          </div>

          {facility.amenities && facility.amenities.length > 0 && (
            <div className="mt-8">
              <h3 className="font-serif text-2xl text-forest">What this home offers</h3>
              <ul className="mt-4 grid gap-2 sm:grid-cols-2">
                {facility.amenities.map((a) => (
                  <li key={a} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 text-sage" />
                    <span className="text-foreground">{a}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <Card className="mt-10 rounded-2xl border border-border/60 bg-card p-6">
            <h3 className="font-serif text-2xl text-forest">Visit details</h3>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <p className="flex items-start gap-2 text-sm"><Clock className="mt-0.5 h-4 w-4 text-primary" /><span><b className="text-foreground">Hours:</b> <span className="text-muted-foreground">{facility.hours || "Contact facility"}</span></span></p>
              <p className="flex items-start gap-2 text-sm"><DollarSign className="mt-0.5 h-4 w-4 text-primary" /><span><b className="text-foreground">Price range:</b> <span className="text-muted-foreground">{facility.price_range || "Contact facility"}</span></span></p>
              <p className="flex items-start gap-2 text-sm"><Phone className="mt-0.5 h-4 w-4 text-primary" /><span className="text-muted-foreground">{facility.phone || "—"}</span></p>
              <p className="flex items-start gap-2 text-sm"><Mail className="mt-0.5 h-4 w-4 text-primary" /><span className="text-muted-foreground">{facility.email || "—"}</span></p>
            </div>
          </Card>
        </div>

        <div className="lg:col-span-1">
          <Card className="sticky top-24 rounded-2xl border border-border/60 bg-card p-6 shadow-sm" data-testid="inquiry-card">
            <h3 className="font-serif text-2xl text-forest">Request a visit</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Send a no-pressure inquiry — drop-in, respite, or a trial day.
            </p>
            {submitted ? (
              <div className="mt-6 rounded-xl border border-sage/40 bg-secondary/60 p-4 text-sm" data-testid="inquiry-success">
                <p className="font-medium text-forest">Inquiry sent.</p>
                <p className="mt-1 text-muted-foreground">
                  The facility will get back to you at the contact info on your account.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-5 space-y-4" data-testid="inquiry-form">
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Type of care</label>
                  <select
                    required
                    value={form.care_type}
                    onChange={(e) => setForm({ ...form, care_type: e.target.value })}
                    data-testid="inquiry-care-type"
                    className="mt-1 h-11 w-full rounded-xl border border-border bg-background px-3 text-sm"
                  >
                    {CARE_TYPES.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Preferred date</label>
                  <Input
                    type="date"
                    value={form.requested_date}
                    onChange={(e) => setForm({ ...form, requested_date: e.target.value })}
                    data-testid="inquiry-date"
                    className="mt-1 h-11"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Loved one’s first name</label>
                  <Input
                    placeholder="e.g. Margaret"
                    value={form.loved_one_name}
                    onChange={(e) => setForm({ ...form, loved_one_name: e.target.value })}
                    data-testid="inquiry-loved-one"
                    className="mt-1 h-11"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Message</label>
                  <Textarea
                    rows={4}
                    placeholder="Share anything that helps them prepare — diagnosis, mobility, favorite things…"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    data-testid="inquiry-message"
                    className="mt-1"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="inquiry-submit-btn"
                  className="w-full rounded-full bg-primary py-6 text-primary-foreground"
                >
                  <Heart className="mr-2 h-4 w-4" /> {submitting ? "Sending…" : (user ? "Send inquiry" : "Sign in to send")}
                </Button>
                {!user && (
                  <p className="text-center text-xs text-muted-foreground">
                    You’ll be asked to sign in or create a caregiver account.
                  </p>
                )}
              </form>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
